import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import Button from '@agilemd/ui-button-legacy';
import { addVariants, isElementVisible } from '@agilemd/ui-utils';

import TabContext from '../Context.ts';
import ScrollContext from '../List/ScrollContext.ts';
import styles from './styles.module.scss';

export const COLORS = ['white', 'light'];
export const DIRECTIONS = ['horizontal', 'vertical'];
export const VARIANTS = DIRECTIONS.concat(['default', 'border', 'no-border', 'border-dark']);
const BUTTON_WIDTH = 32;

const Item = ({
  children,
  value = null,
  variant = 'horizontal',
  color = 'white',
  className = '',
  onClick = null,
  isVisible: shouldShowTab = true,
  isDisabled = false,
  ...props
}) => {
  const buttonRef = useRef(null);
  const directionVariant = useMemo(
    () => [].concat(variant).find(v => DIRECTIONS.includes(v)) || 'horizontal',
    [variant]
  );
  const { setTab, isSelected } = useContext(TabContext);
  const { offset, setOffset, canOverscroll } = useContext(ScrollContext);
  const isActive = isSelected(value);
  const setCurrentTab = useCallback(() => {
    setTab(value);

    if (onClick) {
      onClick(value);
    }
  }, [value, onClick, setTab]);
  useEffect(() => {
    if (isActive && canOverscroll && buttonRef.current) {
      const { isVisible } = isElementVisible(buttonRef.current);

      if (!isVisible) {
        setOffset(
          window.innerWidth - buttonRef.current.getBoundingClientRect().right - BUTTON_WIDTH
        );
      }
    }
  }, [isActive, canOverscroll, setOffset]);

  if (!shouldShowTab) {
    return null;
  }

  return (
    <Button
      ref={buttonRef}
      className={classnames(
        addVariants(
          'aglmd-tabs__item',
          styles,
          color,
          variant,
          directionVariant,
          isActive ? 'selected' : 'deselected',
          isDisabled && 'disabled'
        ),
        className
      )}
      onClick={setCurrentTab}
      variant={['flat', 'square']}
      color={color}
      component="li"
      isDisabled={isDisabled}
      style={{
        transform: `translateX(${offset}px)`,
        msTransform: `translateX(${offset}px)`
      }}
      {...props}
    >
      <span className={addVariants('aglmd-tabs__item__container', styles)}>{children}</span>
    </Button>
  );
};

Item.propTypes = {
  /**
   * Fires when the tab is clicked
   * Passes its index as first parameter
   */
  onClick: PropTypes.func,
  /**
   * The inner content of the tab item
   */
  children: PropTypes.node.isRequired,
  /**
   * The value of this element that corresponds to its pane
   */
  value: PropTypes.any,
  /**
   * Variant to use
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Color of the TabItem
   */
  color: PropTypes.oneOf(COLORS),
  /**
   * Class to apply
   */
  className: PropTypes.string,
  isVisible: PropTypes.bool,
  isDisabled: PropTypes.bool
};

export default Item;
